import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Shield, Beaker, BarChart3, Zap, Calculator, Lightbulb, Send, Thermometer, Search, Building2, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const IT_IMAGE = '/previews/shewhart.png';
const CALC_IMAGE = '/previews/carbonation.png';

export const AppsLanding = () => {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState({ email: '', message: '' });
  const [submitState, setSubmitState] = useState('idle');
  const sectionRefs = { engineering: useRef(null), suggest: useRef(null) };

  const scrollTo = (id) => {
    sectionRefs[id]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSuggestion = async (e) => {
    e.preventDefault();
    setSubmitState('sending');
    try {
      const formData = new FormData();
      formData.append('email', suggestion.email);
      formData.append('message', suggestion.message);
      formData.append('_subject', 'App Suggestion from Durable Designs');
      const res = await fetch('https://formspree.io/f/mjgpyqjv', { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
      if (res.ok) {
        setSubmitState('sent');
        setSuggestion({ email: '', message: '' });
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Hero */}
      <section className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide mb-6" style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.2)' }}>
            <Beaker className="w-3.5 h-3.5" /> {t('apps.badge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[1.08] mb-5" data-testid="apps-heading">
            {t('apps.heading')}
          </h1>
          <p className="text-base sm:text-lg text-neutral-400 leading-relaxed max-w-2xl mb-2">
            {t('apps.subtitle')}
          </p>
        </div>
      </section>

      {/* Apps Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="apps-grid">
        <div ref={sectionRefs.engineering} className="max-w-5xl mx-auto space-y-16">

          {/* App 0: Atmospheric Corrosivity Calculator (NEW) */}
          <div className="group" data-testid="app-corrosivity">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg bg-gradient-to-br from-emerald-50 to-emerald-100 aspect-[4/3] flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-600 text-white mb-4">
                      <Search className="w-7 h-7" />
                    </div>
                    <div className="text-2xl font-bold text-emerald-900 mb-1">C1 → CX</div>
                    <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                      ISO 9223:2012 · Atmospheric corrosivity
                    </div>
                    <div className="mt-4 flex justify-center gap-1.5">
                      {['#10B981','#84CC16','#F59E0B','#EF4444','#B91C1C','#7F1D1D'].map((c, i) => (
                        <span key={i} className="w-6 h-6 rounded" style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-emerald-50 -z-10 rounded-lg" />
              </div>
              <div className="order-1 lg:order-2">
                <div className="text-xs font-medium tracking-[0.1em] uppercase text-emerald-700 mb-4">
                  Engineering · Atmospheric exposure
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-4">
                  Corrosivity Calculator
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                  Type any city worldwide. We resolve climate, SO₂ and chloride
                  exposure, then return the ISO 9223:2012 corrosivity class
                  (C1–CX) and corrosion rate for steel, zinc, copper and
                  aluminium — with a stamped PDF report.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { icon: Shield, label: 'ISO 9223:2012' },
                    { icon: Calculator, label: 'Dose-response' },
                    { icon: Search, label: 'Worldwide cities' },
                  ].map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-600">
                      <tag.icon className="w-3.5 h-3.5 text-emerald-600" /> {tag.label}
                    </span>
                  ))}
                </div>
                <Link to="/engineering/calculator/corrosivity" data-testid="app-corrosivity-cta">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 text-sm font-semibold">
                    Open Calculator <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* App 1: Carbonation Calculator */}
          <div className="group" data-testid="app-carbonation">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg">
                  <img src={CALC_IMAGE} alt="Carbonation Durability Calculator" className="w-full h-auto" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-50 -z-10 rounded-lg" />
              </div>
              <div className="order-1 lg:order-2">
                <div className="text-xs font-medium tracking-[0.1em] uppercase text-blue-700 mb-4">
                  {t('apps.calc_badge')}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-4">
                  {t('apps.calc_title')}
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">{t('apps.calc_desc')}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { icon: Calculator, label: 'fib Model Code' },
                    { icon: Shield, label: 'BS 8500-1' },
                    { icon: Zap, label: t('apps.calc_tag_prob') },
                  ].map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-600">
                      <tag.icon className="w-3.5 h-3.5 text-blue-600" /> {tag.label}
                    </span>
                  ))}
                </div>
                <Link to="/engineering/calculator/carbonation" data-testid="app-calc-cta">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 text-sm font-semibold">
                    {t('apps.calc_cta')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* App 3: Mass Concrete Temperature Predictor */}
          <div className="group" data-testid="app-thermal">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.1em] uppercase text-red-700 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Thermal Analysis
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-4">
                  Mass Concrete Temperature Predictor
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                  Estimate peak temperatures and thermal differentials in pile caps and mass foundations.
                  Identify DEF risk and early-age thermal cracking potential before casting — select your
                  city, define your mix, and get instant results.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { icon: Thermometer, label: 'DEF & Cracking Risk', color: 'text-red-600' },
                    { icon: Calculator, label: 'Finite Difference', color: 'text-red-600' },
                    { icon: Zap, label: 'Climate-Aware', color: 'text-red-600' },
                  ].map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-600">
                      <tag.icon className={`w-3.5 h-3.5 ${tag.color}`} /> {tag.label}
                    </span>
                  ))}
                </div>
                <Link to="/engineering/calculator/thermal" data-testid="app-thermal-cta">
                  <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 text-sm font-semibold">
                    Try Temperature Predictor <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg bg-gradient-to-br from-red-50 to-orange-50 p-8 flex flex-col items-center justify-center min-h-[260px]">
                  <Thermometer className="w-16 h-16 text-red-400 mb-4" />
                  <p className="text-sm font-semibold text-gray-700 mb-1">Thermal Analysis</p>
                  <p className="text-xs text-gray-400">1D finite difference — pile caps &amp; mass foundations</p>
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-red-50 -z-10 rounded-lg" />
              </div>
            </div>
          </div>

          {/* App 5: Prioris Platform */}
          <div className="group" data-testid="app-prioris-platform">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.1em] uppercase text-emerald-700 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Full Platform
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-4">
                  Prioris
                </h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                  Access the full Prioris platform — Materials Approval, Quality Control,
                  and all modules in one unified workspace. Manage projects, run compliance checks,
                  and generate audit-ready reports.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { icon: Building2, label: 'Unified Platform', color: 'text-emerald-600' },
                    { icon: FlaskConical, label: 'All Modules', color: 'text-emerald-600' },
                    { icon: Shield, label: 'Compliance Engine', color: 'text-emerald-600' },
                  ].map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-600">
                      <tag.icon className={`w-3.5 h-3.5 ${tag.color}`} /> {tag.label}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <Link to="/prioris" data-testid="app-prioris-cta">
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 text-sm font-semibold">
                      Learn more <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/prioris#contact" className="px-3 py-1 text-xs font-semibold rounded-full hover:opacity-80 transition-opacity" style={{ background: 'rgba(16,185,129,0.1)', color: '#059669' }} data-testid="app-prioris-beta-badge">
                    Beta · Request demo
                  </Link>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50 p-8 flex flex-col items-center justify-center min-h-[260px]">
                  <Building2 className="w-16 h-16 text-emerald-400 mb-4" />
                  <p className="text-sm font-semibold text-gray-700 mb-1">Prioris Platform</p>
                  <p className="text-xs text-gray-400">Materials Approval + Quality Control + more</p>
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-emerald-50 -z-10 rounded-lg" />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Suggest an App / Improvement */}
      <section ref={sectionRefs.suggest} className="scroll-mt-28 py-16 px-4 sm:px-6 lg:px-8" style={{ background: '#f8fafb' }} data-testid="apps-suggestion">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-4" style={{ background: 'rgba(16,185,129,0.1)' }}>
              <Lightbulb className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight mb-2">
              {t('apps.suggest_heading')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('apps.suggest_desc')}
            </p>
          </div>

          {submitState === 'sent' ? (
            <div className="text-center py-8 px-6 rounded-lg" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }} data-testid="suggestion-success">
              <Send className="w-6 h-6 text-emerald-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-800">{t('apps.suggest_thanks')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('apps.suggest_thanks_desc')}</p>
            </div>
          ) : (
            <form onSubmit={handleSuggestion} className="space-y-4" data-testid="suggestion-form">
              <div>
                <Input
                  type="email"
                  required
                  placeholder={t('apps.suggest_email_placeholder')}
                  value={suggestion.email}
                  onChange={(e) => setSuggestion(prev => ({ ...prev, email: e.target.value }))}
                  className="border-gray-200 text-sm"
                  data-testid="suggestion-email"
                />
              </div>
              <div>
                <textarea
                  required
                  rows={4}
                  placeholder={t('apps.suggest_message_placeholder')}
                  value={suggestion.message}
                  onChange={(e) => setSuggestion(prev => ({ ...prev, message: e.target.value }))}
                  className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  data-testid="suggestion-message"
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">{t('apps.suggest_note')}</p>
                <Button
                  type="submit"
                  disabled={submitState === 'sending'}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 text-sm font-semibold"
                  data-testid="suggestion-submit"
                >
                  {submitState === 'sending' ? t('apps.suggest_sending') : <>{t('apps.suggest_cta')} <Send className="w-3.5 h-3.5 ml-2" /></>}
                </Button>
              </div>
              {submitState === 'error' && (
                <p className="text-xs text-red-500 text-center">{t('apps.suggest_error')}</p>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-lg font-bold text-gray-900 tracking-tight mb-2">
            {t('apps.bottom_heading')}
          </h2>
          <p className="text-sm text-gray-500 mb-6">{t('apps.bottom_desc')}</p>
          <Link to="/engineering/contact" data-testid="apps-contact-cta">
            <Button variant="outline" className="border-gray-300 text-gray-700 px-6 py-2.5 text-sm font-semibold">
              {t('apps.bottom_cta')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
